import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { AppButton, Box, ErrorModal, Inscription, Preloader, WarningIcon } from '@common-fe/common-fe';
import { toString } from 'lodash';

import { WarnModal } from '@/components/elements';
import Tip from '@/components/elements/Tip/Tip';
import { useSnackbar } from '@/modules/core';

import { MAIN_MODAL_WRAPPER_ID } from '../../../AddModalWrapper';
import { ContactResponseModel } from '../ContactSecurity.types';

import { useUnassignAllAccountManagerRolesQuery } from './useUnassignAllAccountManagerRoles.query';

const More: React.FC<{ employers?: string[]}> = ({ employers }) => {
  if (!employers?.length) {
    return null;
  }

  return (
    <Box direction="row">
      <Inscription color="textBody" textAlign="center" lineHeight="20px">...</Inscription>
      <Tip
        hint={(
          <Box
            pad={{ left: 'spacing8' }}
            height={{ max: '200px' }}
            overflow="auto"
            style={{ cursor: 'auto' }}
          >
            {employers.map((item) => (
              <Inscription
                key={item}
                color="textOnColor"
                size="small"
                style={{ whiteSpace: 'normal' }}
                weight={500}
                lineHeight="18px"
              >
                &bull;&nbsp;&nbsp;{item}
              </Inscription>
            ))}
          </Box>
        )}
        space="38px"
      >
        <Inscription
          style={{ paddingRight: '24px' }}
          margin={{ left: 'spacing4' }}
          color="textAccent"
        >
          +{employers.length}
        </Inscription>
      </Tip>
    </Box>
  );
};

interface Props {
  close: () => void;
  contact?: Partial<ContactResponseModel>;
  names?: string[];
  onSuccess?: () => void;
}

export const RemovingAccountManagerAssignmentPopup: React.FC<Props> = ({ close, contact, names, onSuccess }) => {
  const fullName = useMemo(() => `${contact?.first_name || ''} ${contact?.last_name || ''}`.trim(),
    [contact]);
  const [error, setError] = useState('');
  const { handleAddPermanentSnackbar } = useSnackbar();
  const handleSuccess = useCallback(() => {
    if (onSuccess) {
      onSuccess();
    }

    handleAddPermanentSnackbar({
      text: `${fullName} is no longer assigned as an account manager to any organizations.`,
      closeIcon: true,
      zIndex: 2001,
    });
    close();
  }, [onSuccess, fullName, handleAddPermanentSnackbar, close]);
  const handleError = useCallback((errorText?: string) => setError(errorText || ''), [setError]);
  const { mutateAsync, isLoading } = useUnassignAllAccountManagerRolesQuery(
    toString(contact?.id),
    handleSuccess,
    handleError,
  );
  const showedNames = useMemo(() => names?.slice(0, 3)?.join(', '), [names]);

  const confirm = useCallback(() => {
    mutateAsync();
  }, [mutateAsync]);

  useEffect(() => {
    const modalElement = document.getElementById(MAIN_MODAL_WRAPPER_ID);

    if (modalElement) {
      modalElement.style.overflow = 'hidden';
    }

    return () => {
      if (modalElement) {
      modalElement.style.overflow = '';
    }
    };
  }, []);

  if (error) {
    return (
      <ErrorModal
        testId="RemovingAccountManagerAssignmentPopup_error"
        visible
        header="Error"
        helpText={error}
        buttonText="Close"
        onSetVisible={() => {
          setError('');
        }}
      />
    );
  }

  return (
    <WarnModal
      testId="RemovingAccountManagerAssignment"
      visible
      iconNode={<WarningIcon size="48px" />}
      onSetVisible={() => close()}
      isButtonsHidden
    >
      <Box pad={{ horizontal: 'spacing24', vertical: 'spacing12' }}>
        <Inscription color="textTitle" size="24px" weight="bold" textAlign="center">
          {`Remove ${fullName} assignment as an Account Manager?`}
        </Inscription>
      </Box>
      <Box margin={{ bottom: 'spacing12' }}>
        <Inscription color="textBody" textAlign="center" lineHeight="20px">
          This user is assigned as an account manger to the following organizations:
        </Inscription>
      </Box>
      <Box direction="row">
        <Inscription
          color="textBody"
          textAlign="center"
          lineHeight="20px"
        >
          {showedNames}
        </Inscription>
        <More employers={names?.slice(3)}/>
      </Box>
      <Box direction="row" justify="center" margin={{ top: 'spacing24' }}>
        <AppButton
          buttonType="secondary"
          width="150px"
          testId="RemovingAccountManagerAssignment_cancel"
          onClick={close}
        >
          Cancel
        </AppButton>
        <Box margin={{ left: 'spacing12' }}>
          <AppButton
            width="150px"
            color="red"
            testId="RemovingAccountManagerAssignment_confirm"
            onClick={confirm}
            disabled={isLoading}
          >
            {isLoading ? (
              <Preloader />
            ) : 'Confirm'}
          </AppButton>
        </Box>
      </Box>
    </WarnModal>
  );
};
